import PropTypes from "prop-types";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  BaseForm,
  buildCustomFieldSelection,
  CustomFieldsSection,
  FormChipList,
  FormTag,
  SectionCard,
} from "components/BaseForm";
import { formatError } from "helpers/formatError";
import { useEntityMutations } from "hooks/useEntity";
import { SUCCESS_MESSAGES, TAG_MODEL_TYPE } from "routes/entity/form/constants";
import { buildDefaultValues, FORM_MODES } from "routes/entity/form/defaults";
import { toEntityPayload } from "routes/entity/form/payload";
import { buildEntitySchema } from "routes/entity/form/schema";
import DetailsSection from "routes/entity/form/sections/DetailsSection";
import EntityAddressesSection from "routes/entity/form/sections/EntityAddressesSection";

const toAccountManagerDisplay = record =>
  (record?.account_managers ?? []).map(({ user_id, user_full_name }) => ({
    id: user_id,
    label: user_full_name,
  }));

/**
 * The Entity create and edit form. Its caller loads every input to the
 * defaults before mounting it, because BaseForm reads them once.
 */
const EntityForm = ({
  mode,
  record,
  customFieldConfigs,
  customFieldGroups = [],
  onSaved,
  onCancel,
}) => {
  const { create, update } = useEntityMutations();
  const savedRef = useRef(null);
  const isEdit = mode === FORM_MODES.edit;

  const [customFieldSelection, setCustomFieldSelection] = useState(() =>
    buildCustomFieldSelection(
      customFieldConfigs,
      record ? (record.custom_fields ?? {}) : null
    )
  );

  const schema = useMemo(
    () => buildEntitySchema({ customFieldConfigs }),
    [customFieldConfigs]
  );

  const defaultValues = useMemo(
    () => buildDefaultValues({ mode, record, customFieldConfigs }),
    [mode, record, customFieldConfigs]
  );

  const accountManagerDisplay = useMemo(
    () => toAccountManagerDisplay(record),
    [record]
  );

  const handleSubmit = async values => {
    const data = toEntityPayload(values, { customFieldConfigs });
    try {
      savedRef.current = isEdit
        ? await update.mutateAsync({ id: record.id, data })
        : await create.mutateAsync(data);
    } catch (error) {
      // BaseForm shows the banner and field errors from the rethrown error.
      toast.error(formatError(error, "Could not save the Entity"));
      throw error;
    }
  };

  const handleSuccess = () => {
    toast.success(SUCCESS_MESSAGES[mode]);
    onSaved(isEdit ? record : savedRef.current);
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
      onCancel={onCancel}
    >
      <div className="tw:flex tw:flex-col tw:gap-6">
        <DetailsSection accountManagerDisplay={accountManagerDisplay} />
        <EntityAddressesSection />
        <SectionCard title="Alternate Names">
          <div className="tw:max-w-2xl">
            <FormChipList
              name="alternate_names"
              placeholder="Type a name and press Enter"
            />
          </div>
        </SectionCard>
        <SectionCard title="Tags">
          <div className="tw:max-w-2xl">
            <FormTag name="tags" label={null} modelType={TAG_MODEL_TYPE} />
          </div>
        </SectionCard>
        {customFieldSelection.length > 0 ? (
          <CustomFieldsSection
            selection={customFieldSelection}
            setSelection={setCustomFieldSelection}
            groups={customFieldGroups}
          />
        ) : null}
      </div>
    </BaseForm>
  );
};

EntityForm.propTypes = {
  mode: PropTypes.oneOf(Object.values(FORM_MODES)).isRequired,
  record: PropTypes.object,
  customFieldConfigs: PropTypes.array.isRequired,
  customFieldGroups: PropTypes.array,
  onSaved: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
};

export default EntityForm;
